// Copied from DealerFlow 360 (dealerflow360/web/src/shared/payments/cardValidation.ts); spec references are DF360's.
import type { FieldErrors } from './fieldErrors';
import type { AcceptCardData } from './acceptJs';

/**
 * The card form's values. The card number and CVV live only in the card component's state and
 * are cleared once Accept.js has them (R23); the name and billing address are not secret.
 */
export interface CardFieldValues {
  cardNumber: string;
  expiration: string;
  cvv: string;
  nameOnCard: string;
  street: string;
  city: string;
  state: string;
  zip: string;
}

export type CardField = keyof CardFieldValues;

/** The name and billing address: not secret, so they may live with a payment draft. */
export type CardBilling = Pick<CardFieldValues, 'nameOnCard' | 'street' | 'city' | 'state' | 'zip'>;

export const EMPTY_BILLING: CardBilling = { nameOnCard: '', street: '', city: '', state: '', zip: '' };

export const digitsOnly = (value: string) => value.replace(/\D/g, '');

/** American Express numbers start 34 or 37: 15 digits grouped 4-6-5, with a 4-digit CVV. */
export const isAmex = (cardNumber: string) => /^3[47]/.test(digitsOnly(cardNumber));

/** The CVV's length for the card number typed: 4 digits for American Express, 3 for any other card. */
export const cvvLength = (cardNumber: string) => (isAmex(cardNumber) ? 4 : 3);

/**
 * A card number as typed, grouped as the card prints it: American Express 4-6-5 ("3782 822463 10005"), any
 * other card in fours ("4111 1111 1111 1111", up to 19 digits).
 */
export function formatCardNumber(value: string): string {
  const digits = digitsOnly(value);
  if (isAmex(digits)) {
    const amex = digits.slice(0, 15);
    return [amex.slice(0, 4), amex.slice(4, 10), amex.slice(10)].filter(Boolean).join(' ');
  }
  return digits.slice(0, 19).replace(/(\d{4})(?=\d)/g, '$1 ');
}

/**
 * An expiration as typed, with the slash put in once the year starts: "1230" → "12/30". A slash typed after a
 * one-digit month stays where it is ("1/30"), and a two-digit month alone stays as typed, so deleting back over
 * the slash isn't undone. Up to MM/YYYY.
 */
export function formatExpiration(value: string): string {
  const slash = value.indexOf('/');
  if (slash >= 0) return `${digitsOnly(value.slice(0, slash)).slice(0, 2)}/${digitsOnly(value.slice(slash + 1)).slice(0, 4)}`;
  const digits = digitsOnly(value).slice(0, 6);
  return digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
}

/** Luhn checksum over the digits. */
export function passesLuhn(digits: string): boolean {
  let sum = 0;
  let double = false;
  for (let index = digits.length - 1; index >= 0; index -= 1) {
    let digit = digits.charCodeAt(index) - 48;
    if (digit < 0 || digit > 9) return false;
    if (double) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    double = !double;
  }
  return digits.length > 0 && sum % 10 === 0;
}

/**
 * "MM/YY" or "MM/YYYY" (spaces allowed around the slash) as a two-digit month and four-digit
 * year, or null when it is neither.
 */
export function parseExpiration(value: string): { month: string; year: string } | null {
  const match = /^\s*(\d{1,2})\s*\/\s*(\d{2}|\d{4})\s*$/.exec(value);
  if (!match) return null;
  const month = Number(match[1]);
  if (month < 1 || month > 12) return null;
  const year = match[2]!.length === 2 ? 2000 + Number(match[2]) : Number(match[2]);
  return { month: String(month).padStart(2, '0'), year: String(year) };
}

/** A card is good through the last day of its expiration month. */
export function isExpired(expiration: { month: string; year: string }, today = new Date()): boolean {
  const year = Number(expiration.year);
  const month = Number(expiration.month);
  const nowYear = today.getFullYear();
  const nowMonth = today.getMonth() + 1;
  return year < nowYear || (year === nowYear && month < nowMonth);
}

const ZIP = /^\d{5}(-?\d{4})?$/;

/** The card form's messages, by field. Empty when the card can be sent to Authorize.Net. */
export function cardFieldErrors(values: CardFieldValues, today = new Date()): FieldErrors<CardFieldValues> {
  const errors: FieldErrors<CardFieldValues> = {};
  const number = digitsOnly(values.cardNumber);
  if (!number) errors.cardNumber = 'Enter the card number.';
  else if (number.length < 13 || number.length > 19 || !passesLuhn(number))
    errors.cardNumber = 'This card number is not valid.';

  const expiration = values.expiration.trim();
  const parsed = parseExpiration(expiration);
  if (!expiration) errors.expiration = 'Enter the expiration date.';
  else if (!parsed) errors.expiration = 'Enter the expiration as MM/YY.';
  else if (isExpired(parsed, today)) errors.expiration = 'This card has expired.';

  const cvv = values.cvv.trim();
  const length = cvvLength(values.cardNumber);
  if (!cvv) errors.cvv = 'Enter the CVV.';
  else if (cvv.length !== length || digitsOnly(cvv) !== cvv) errors.cvv = `The CVV is ${length} digits on this card.`;

  if (!values.nameOnCard.trim()) errors.nameOnCard = 'Enter the name on the card.';
  const state = values.state.trim();
  if (state && !/^[A-Za-z]{2}$/.test(state)) errors.state = 'Use the 2-letter state code.';
  const zip = values.zip.trim();
  if (!zip) errors.zip = 'Enter the billing ZIP code.';
  else if (!ZIP.test(zip)) errors.zip = 'Enter a 5-digit ZIP code.';
  return errors;
}

/** What Accept.js takes (R20, R28). Call only on values `cardFieldErrors` passed. */
export function toAcceptCardData(values: CardFieldValues): AcceptCardData {
  const expiration = parseExpiration(values.expiration)!;
  return {
    cardNumber: digitsOnly(values.cardNumber),
    month: expiration.month,
    year: expiration.year,
    cardCode: values.cvv.trim(),
    zip: values.zip.trim() || undefined,
    fullName: values.nameOnCard.trim() || undefined,
  };
}
